import { useMemo } from "react";
import {
  ArrowsUpDownIcon,
  Box,
  Button,
  Menu,
  PlusIcon,
  SearchInput,
  SelectInput,
  Typography,
  useMenu
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  SPACE_BLOCK,
  belowMobile,
  desktopOnly,
  mobileOnly
} from "../../../components/UserInfo";
import {
  USER_LIST_SORT_LABEL_KEYS,
  USER_LIST_SORT_OPTIONS,
  type UserListSortOption
} from "../../useUserListQuery";

export interface ListToolbarProps {
  filterInput: string;
  sortOption: UserListSortOption;
  onFilterChange: (term: string) => void;
  onSortChange: (option: UserListSortOption) => void;
  onAddUser: () => void;
}

/**
 * The list panel's fixed toolbar: the sort `SelectInput`, the filter `SearchInput`, and **Add User**
 * (FR-012, FR-013, FR-018).
 *
 * **THE ORDER IS NORMATIVE — sort, then filter, then Add User.** It comes from the approved design
 * (`user-manager-ui-prototype.html`'s `um-list-toolbar`, and the ASCII diagram in
 * `contracts/user-list-panel.md`), not from convenience. It shipped **inverted** on 2026-09-11 and
 * was corrected 2026-09-14: the build followed the *prose section order* of the contract document,
 * which lists Filter (FR-012) before Sort (FR-013) because it is ordered by FR number rather than by
 * visual position. **Do not reorder these to match the FR numbers, the prop order, or anything else.**
 * `ListToolbar.spec.tsx` asserts the DOM order, which is the regression anchor whose absence let the
 * original divergence ship unnoticed.
 *
 * **Add User lives here, not in the detail panel**, because it creates a *new* user rather than
 * acting on the selected one — putting it beside the header actions would group it with three
 * controls that all operate on the subject.
 *
 * Do not use a block `Alert` in this cluster: it forces the cluster to the alert's width and wraps
 * the controls.
 */
export const ListToolbar = ({
  filterInput,
  sortOption,
  onFilterChange,
  onSortChange,
  onAddUser
}: ListToolbarProps) => {
  /*
   * Memoized per `.claude/rules/interstate.md`: a freshly-built array on every render fires the
   * component's prop-keyed re-sync effect on unrelated re-renders — and this toolbar re-renders on
   * every keystroke of the filter beside it.
   */
  const sortOptions = useMemo(
    () =>
      USER_LIST_SORT_OPTIONS.map(option => ({
        label: Locale.getText(USER_LIST_SORT_LABEL_KEYS[option]) as string,
        value: option
      })),
    []
  );

  const sortMenuReferenceProps = useMenu();

  /*
   * A named wrapper rather than `onClick={sortMenuReferenceProps.onClick}`, which
   * `react/jsx-handler-names` rejects. The event is forwarded because `useMenu` positions the menu
   * against the element that was clicked.
   */
  const handleSortMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    sortMenuReferenceProps.onClick?.(event);
  };

  /*
   * The same four orders as the desktop `SelectInput`, shaped as `MenuOption`s. Memoized for the
   * same reason `sortOptions` is, and keyed on `sortOption` because the current order is marked
   * `disabled` — selecting the order you are already in is a no-op, and showing it as unavailable is
   * how a menu conveys "this is the current one" when it has no `aria-current` slot.
   */
  const sortMenuOptions = useMemo(
    () =>
      sortOptions.map(option => ({
        disabled: option.value === sortOption,
        id: `usermanager-sort-${option.value}`,
        label: option.label,
        onSelect: () => onSortChange(option.value),
        value: option.value
      })),
    [onSortChange, sortOption, sortOptions]
  );

  return (
    <Box
      data-testid={"user-list-toolbar"}
      sx={{
        /*
         * Below `md` the toolbar is a **row**: search takes the width and sort collapses to an icon
         * beside it (TA4236901 — "search goes first and full width … with 453 users, search is the
         * primary interaction and sort is secondary"). `flex-end` so the icon button's baseline
         * lines up with the input's, which sits under its own label.
         */
        alignItems: "stretch",
        columnGap: "8px",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        /*
         * The prototype's `.um-list-toolbar{padding:0 0 var(--ids-sem-space-3)}` — the gap between
         * the toolbar and the result-count line below it belongs to the toolbar, not to a uniform
         * `rowGap` on the rail. The rail's three gaps are 16 / 8 / 16, so each band owns its own.
         */
        paddingBottom: SPACE_BLOCK,
        rowGap: SPACE_BLOCK,
        ...belowMobile({ alignItems: "flex-end", flexDirection: "row" })
      }}
    >
      {/*
       * ⚠️ **`order` is what reverses search and sort, and there is exactly ONE `SearchInput`.**
       *
       * The obvious implementation — a desktop subtree and a mobile subtree — would put **two**
       * search fields in the DOM carrying the same `aria-label`, which breaks `getByLabelText` in
       * `ListToolbar.spec.tsx` ("found multiple elements") and is genuinely worse for assistive tech
       * than for the tests. The sort control *is* duplicated, because its **form** changes (a
       * `SelectInput` cannot become an icon button in CSS) — but its two shapes take different
       * accessible names ("Sort By" and "Sort"), so nothing can resolve to both.
       */}
      <SelectInput
        data-testid={"user-list-sort"}
        label={Locale.getText("FACET_FIELD_SORT_BY") as string}
        options={sortOptions}
        sx={{ order: 1, ...desktopOnly("block") }}
        value={sortOption}
        onChange={event =>
          onSortChange(String(event?.target?.value ?? "") as UserListSortOption)
        }
      />

      <Box
        sx={{
          flex: "0 1 auto",
          minWidth: 0,
          order: 2,
          ...belowMobile({ flex: 1 })
        }}
      >
        {/*
         * **The filter's label and its placeholder are two different strings, deliberately.** The
         * approved design gives the control a visible label ("Filter Users") *and* a placeholder
         * that names all three searchable fields ("Filter by name, id, or email") — FR-012 requires
         * the latter so the control does not understate what it matches.
         *
         * It shipped on 2026-09-11 with **one** string doing all three jobs (`aria-label`,
         * `placeholder` and `buttonAriaLabel` were all `UM_FILTER_LABEL`), and no visible label at
         * all. The cause was upstream in the spec, not a typo: research R-013 enumerated "the
         * toolbar's **three** controls" — one label key per control — while the prototype needs
         * **four** strings for three controls, because this one carries both. `contracts/
         * user-list-panel.md` reinforced it by requiring only a *placeholder*. Both are corrected.
         *
         * **`Typography tag="label"` with no `htmlFor`, and that is a constraint not an oversight.**
         * `TypographyProps` exposes no `htmlFor` (it declares `id`, `tag`, `variant`, `sx`, `color`,
         * `className` and nothing else), so the visible label cannot be *associated* from here —
         * the same shape of limitation already documented for `ListProps`' missing aria props in
         * `UserListPanel` and for `BoxProps` in `garage`'s `DataTable`. The accessible name
         * therefore still comes from `aria-label`, and it is set to **the same text as the visible
         * label** so the two cannot disagree (WCAG 2.5.3 Label in Name). `getByLabelText` in
         * `ListToolbar.spec.tsx` resolves the input through that `aria-label`.
         */}
        <Typography tag={"label"} variant={"label-md"}>
          {Locale.getText("UM_FILTER_LABEL") as string}
        </Typography>

        {/*
         * **`buttonAriaLabel` is required, and its absence was a real defect.** `SearchInput`
         * renders its own submit button (`search-button-<id>`) with **no accessible name of its
         * own**, so a screen-reader user met an unnamed button — caught by
         * `UserManager.a11y.spec.tsx`'s "every button has an accessible name" case, 2026-09-14.
         * Unlike the `List`/`ListItem` cases there **is** a prop for this one, so it is a plain fix
         * rather than a workaround.
         *
         * It takes `UM_FILTER_LABEL`, not the placeholder: the button's purpose is the input's
         * purpose. Two controls sharing an accessible name is fine when their roles differ — a
         * textbox and a button are not confusable.
         */}
        <SearchInput
          aria-label={Locale.getText("UM_FILTER_LABEL") as string}
          buttonAriaLabel={Locale.getText("UM_FILTER_LABEL") as string}
          data-testid={"user-list-filter"}
          inputValue={filterInput}
          placeholder={Locale.getText("UM_FILTER_PLACEHOLDER") as string}
          onInputChange={event =>
            onFilterChange(String(event?.target?.value ?? ""))
          }
        />
      </Box>

      {/*
       * ## The mobile sort control: an icon button opening a `Menu`
       *
       * **A `Menu`, not a bottom-sheet `SlideOut` — a deliberate deviation from TA4236901**, which
       * asked for "an icon button that opens a bottom sheet". Two reasons:
       *
       * 1. **Consistency within the page.** `HeaderActions` already collapses its secondary actions
       *    into a `Menu` from an icon button below this same breakpoint. Making the *other*
       *    icon-button-reveals-options control a different overlay mechanism means two patterns to
       *    learn on one screen.
       * 2. **`SlideOut position="bottom"` is unexercised in this repo.** Interstate supports the
       *    anchor, but nothing here has ever used it (only `left` and `right`), so it carries
       *    verification risk that a `Menu` — used in four libs — does not.
       *
       * The usual argument for a sheet is thumb reach, and it does not apply: this button sits at
       * the **top** of the list, so its menu opens where the thumb already is. If the reviewer
       * prefers the sheet, `Modal size="largeOnXS"` is Interstate's own mobile-modal variant and the
       * safer of the two unexercised options.
       *
       * The trigger takes `SORT` ("Trier") rather than `FACET_FIELD_SORT_BY` ("Sort By"): a
       * different accessible name from the desktop `SelectInput` is what keeps the two shapes
       * distinguishable to a query and to a screen reader.
       */}
      <Box sx={{ order: 3, position: "relative", ...mobileOnly("block") }}>
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-user-list-sort-mobile",
            eventProperties: undefined
          }}
          aria-haspopup={"true"}
          aria-label={Locale.getText("SORT") as string}
          buttonStyle={"tertiary"}
          data-testid={"user-list-sort-mobile"}
          icon={<ArrowsUpDownIcon title={""} />}
          size={"lg"}
          onClick={handleSortMenuClick}
        />

        <Menu
          menuOptions={sortMenuOptions}
          referenceProps={sortMenuReferenceProps.referenceProps}
        />
      </Box>

      <Box sx={{ order: 4, position: "relative", ...desktopOnly("block") }}>
        {/*
         * **Desktop only (TA4236901).** Below `md` the add-user action lives in the list panel's own
         * header instead — the foot of the list is the pager's, and a full-width bar directly above
         * the pager crowds the one control that has to be hit accurately. See `UserListPanel`'s
         * mobile header.
         *
         * **`block` + `primary` + the plus icon are the approved design, not styling preference.**
         * The prototype renders it `btn-primary btn-block` with a leading `+` glyph — a full-width
         * call to action closing the toolbar. It shipped as an auto-width `secondary` button with no
         * icon, which reads as a minor afterthought beside the two fields above it. Corrected
         * 2026-09-15; `ListToolbar.spec.tsx` anchors the style so it cannot drift back.
         *
         * `title={""}` on the icon follows the repo's established pattern for a decorative icon
         * inside an already-labelled button (`IncentivesManagerHeader`): the `SvgProps` type makes
         * `title` required, and a non-empty one would add a redundant tooltip and accessible name
         * beside the button's own "Add User".
         */}
        <Button
          block
          analyticsConfiguration={{
            eventName: "usermanager-add-user",
            eventProperties: undefined
          }}
          buttonStyle={"primary"}
          data-testid={"user-list-add-user"}
          startIcon={<PlusIcon title={""} />}
          onClick={onAddUser}
        >
          {Locale.getText("ADD_USER") as string}
        </Button>
      </Box>
    </Box>
  );
};
